<template>
<div class="erm-request-project-list">
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="經費需求項目清單"
      subtitle="瀏覽可申請的經費需求項目並提出申請"
      icon="tabler-clipboard-list"
      :count="data.length"
    />

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" md="4">
          <v-select v-model="filters.annual" :items="annualSelect" label="所屬年度" item-value="adYear" item-title="rocYear" clearable></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
          v-model="filters.projectId"
          :items="projectSelect"
          label="經費需求項目"
          item-value="projectId"
          item-title="projectTitle"
          clearable
          no-data-text="無經費需求項目"
          prepend-inner-icon="tabler-search"
          >
          </v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <v-select v-model="filters.isGold" :items="isGoldSelect" label="是否為黃金計畫" item-value="value" item-title="title" clearable></v-select>
        </v-col>
      </v-row>
    </v-card-item>

    <InteractiveDataTable
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      :items-length="data.length"
      :items="pagedItems"
      :headers="headers"
      :loading="loading"
      :row-clickable="false"
      empty-text="尚無經費需求項目"
      empty-subtitle="可調整上方篩選條件後再查詢"
      empty-icon="tabler-clipboard-off"
      :items-per-page-options="[
        {value: 10, title: '10'},
        {value: 25, title: '25'},
        {value: 50, title: '50'},
        {value: 100, title: '100'},
      ]"
    >
      <template #item.projectName="{ item }">
        <div class="project-name-cell" :title="item.projectName">{{ item.projectName }}</div>
      </template>

      <template #item.applyDate="{ item }">
        <div v-if="item.applyDate" class="d-inline-flex flex-column text-medium-emphasis text-no-wrap">
          <span class="d-inline-flex align-center flex-nowrap">
            <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
            {{ splitApplyDate(item.applyDate)[0] }}
          </span>
          <span v-if="splitApplyDate(item.applyDate)[1]" class="d-inline-flex align-center flex-nowrap">
            <VIcon icon="tabler-arrow-down-right" size="16" class="mr-1" />
            {{ splitApplyDate(item.applyDate)[1] }}
          </span>
        </div>
        <span v-else class="text-disabled">—</span>
      </template>

      <template #item.isGoldPlan="{ item }">
        <v-chip
          :color="item.isGoldPlan === 'Y' ? 'amber-darken-2' : 'grey'"
          variant="tonal"
          size="small"
          class="font-weight-bold"
        >
          <VIcon :icon="item.isGoldPlan === 'Y' ? 'tabler-star-filled' : 'tabler-minus'" size="14" start />
          {{ item.isGoldPlan === 'Y' ? '是' : '否' }}
        </v-chip>
      </template>

      <template #item.status="{ item }">
        <Status
          :status="item.status"
          :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
          :is-green="['Approved']"
          :is-warning="['Draft']"
          :is-purple="['ReturnedForRevision', 'MReturnedForRevision', 'FReturnedForRevision', '回覆修正', '競技回覆修正', '財務退回修正', '承辦回覆修正', '財務回覆修正']"
          :is-secondary="['Published', 'SelectedForTeam']"
          :shared-table-id="tabId"
          btn-class="x-large"
          font-class="18px"
          :status-config="getStatusData"
        />
      </template>

      <template #item.actions="{ item }">
        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="tabler-send"
          class="idt-action-btn"
          @click.stop="applyExpenseRequest(item)"
        >
          申請
        </VBtn>
      </template>
    </InteractiveDataTable>
  </v-card>
</div>

</template>



<script setup lang="ts">
import Status from '../tnm/StatusComponenet.vue'
import {onMounted,reactive} from "vue";
  definePage({
    meta: {
      action: 'manage',
      subject: 'EXPENSEREQUESTPROJECTLIST',
    },
  })
  const router= useRouter()
// 登入者資料
const userData = useStorage('userData', {}).value;

  const filters = reactive({
    annual: null,
    projectId: null,
    isGold:null
  });

  const headers = ref([
    { title: "項目名稱", key: "projectName", icon: "tabler-clipboard-text", width: 320, maxWidth: 320 },
    { title: "公文號", key: "docNo", icon: "tabler-file-text" },
    { title: "所屬年度", key: "annual", icon: "tabler-calendar" },
    { title: "申請期間", key: "applyDate", icon: "tabler-calendar-event" },
    { title: "黃金計畫", key: "isGoldPlan", icon: "tabler-star" },
    { title: "狀態", key: "status", icon: "tabler-flag" },
    { title: '操作', key: 'actions', sortable: false, icon: "tabler-settings" }
  ]);

  // 後端把申請期間組成「起~訖」單一字串，前端拆成兩行顯示
  const splitApplyDate = (value: string) => {
    return String(value ?? '').split('~').map(s => s.trim())
  }

  const isGoldSelect=[{title:"是",value:"Y"}, {title:"否",value:"N"}]
  const { data: yearList, execute: fetchFilterYearList } = await useApi<any>(createUrl('/tca/expenserequestprojectlist/getYear', {}))
  const annualSelect =computed(()=>yearList?yearList.value.data:0)
  const projectSelect =  reactive( [])
  const getProjectList = async () => {
    let deptNo = userData.depNo;
    let annual = filters.annual ? filters.annual : null;
    let isGold = filters.isGold ? filters.isGold : null;
    await $api('/tca/expenserequestprojectlist/getAllProjectListAsso', {
      method: 'POST',
      body: {
        deptNo:deptNo,
        isGold,
        annual,
        status:"Approved"
      }
    }).then((response) => {
      console.log("response=", response);
      if (response && response.data && response.data.length > 0) {
        console.log("response=", response);
        projectSelect.splice(0, projectSelect.length, ...response.data);
        console.log("projectSelect=", projectSelect);
      } else {
        projectSelect.splice(0, projectSelect.length);  
        console.error('Fetch error: no data received or response structure unexpected');
      }
    }).catch((error) => {
      projectSelect.splice(0, projectSelect.length); 
      console.error('Error during fetch:', error);
    });
  };

  // const expenseRequestProject = (item) =>{
  //   router.push({ path: '/tca/erm/ExpenseRequestProject', query: { projectId: item.projectId } })
  // }

  const applyExpenseRequest = (item) =>{
    if(item.isGoldPlan==="N"){
      router.push({ path: '/tca/erm/ExpenseRequest',query:{projectId:item.projectId}});
    }else{
      router.push({ path: '/tca/erm/GoldPlanApply',query:{projectId:item.projectId}});
    }
  }

  const page = ref(1);
  const itemsPerPage = ref(100);
  const totalData = ref(0);
  const data = ref([]);
  const loading = ref(false);

  // client 端分頁切片（資料一次抓回，前端分頁）
  const pagedItems = computed(() => {
    if (itemsPerPage.value === -1) return data.value
    const start = (page.value - 1) * itemsPerPage.value

    return data.value.slice(start, start + itemsPerPage.value)
  })

  const fetchData = async () => {
    const annual = filters.annual ? filters.annual : null;
    const projectId = filters.projectId ? filters.projectId : null;
    // const status = filters.status ? filters.status : null;
    const isGold = filters.isGold?filters.isGold:null;
    const deptNo = userData.depNo;

    page.value = 1;
    loading.value = true;
    await $api('/tca/expenserequestprojectlist/getAssoList', {
      method: 'POST',
      body: {
        annual,
        deptNo:deptNo,
        projectId,
        isGold,
        page : 0,
        size : 10000
      }
    }).then((response) => {
      data.value = response;
      totalData.value = response.length;
    }).catch((error) => {
      data.value=[]
      console.error('查詢失敗:', error);
    }).finally(() => {
      loading.value = false;
    });
  };

  const tabId = ref('ExpenseRequestProject')
  const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
    query: {
      tabId: tabId.value
    },
  }))

  watch(()=>filters.annual,()=>{
    getProjectList()
  })
  watch(filters, () => {
    fetchData();
  })

  onMounted(() => {
    getProjectList();
    fetchData();
  })

</script>

<style scoped>
/* 限制「項目名稱」欄寬，內容過長時換行而非撐寬整個表格 */
.erm-request-project-list :deep(.project-name-cell) {
  max-width: 320px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}
</style>
